import { CommonModule, KeyValue } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { LazyLoadEvent } from 'primeng/api';
import { finalize, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { AppService } from 'src/app/services/app.service';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import { LocalizationService, LocalizedNumberFormat } from 'src/app/shared/localization/localization.service';
import { IPaginatedSearch } from 'src/app/shared/models';
import { ICaseStatusDetail, ICasesStatusDetailsRequest, ICasesStatusRequest, ICustomerCasesStatusSummary, STATUS_REPORT_DETAILS_COLUMNS, STATUS_REPORT_RESULT_COLUMNS } from '../reports-models';
import { ReportsService } from '../reports.service';
import { Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { CustomMultiSelectComponent } from 'src/app/shared/custom-multi-select/custom-multi-select.component';
import { ButtonModule } from 'primeng/button';
import { SettingsService } from "src/app/services/settings.service";

@Component({
  selector: 'app-status',
  templateUrl: './status.component.html',
  styleUrls: ['./status.component.scss'],
  imports: [CommonModule, TranslateModule, TableModule, CustomMultiSelectComponent, ButtonModule]
})
export class StatusComponent implements OnInit {
  customerNumber$ = this.appService.customerNumber$;
  customers$: Observable<KeyValue<number, string>[]>;
  selectedCustomers: number[] = [];
  statusesSummary: ICustomerCasesStatusSummary[];
  statusesSummaryTotals: ICustomerCasesStatusSummary;

  numberOfRowsPerPage = 20;
  currentPageNumber = 1;
  totalNumberOfItems = 0;
  totalStatuses: string;
  userAssignedCustomersCount : number;

  numberFormat: LocalizedNumberFormat;
  columns = STATUS_REPORT_RESULT_COLUMNS;
  subColumns = [...STATUS_REPORT_DETAILS_COLUMNS];

  canSearch: boolean;
  showResults: boolean;
  isLoading: boolean;

  private readonly unsubscribe$ = new Subject();


  constructor(
    private appService: AppService,
    private reportsService: ReportsService,
    private notifications: MessageToUiService,
    private spinnerService: GlobalSpinnerService,
    private localisationService: LocalizationService,
    private translateService: TranslateService,
    private settingsService: SettingsService,
    private router : Router
  ) {

  }
  ngOnInit(): void {
    this.spinnerService.show();
    this.initializeCustomerList();
    this.localisationService.settings$.subscribe(
      (_) => (this.numberFormat = _.numberPipeFormat)
    );
    this.settingsService.getSettingValueByName('AreRef2Ref3Inlcuded')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((areRef2Ref3Inlcuded) => {
        if (!areRef2Ref3Inlcuded.isTruthy()) {
          this.subColumns = this.subColumns.filter(
            col => col.name !== 'customerRef2' && col.name !== 'customerRef3'
          );
        }
      });
    
    this.spinnerService.hide();
  }

  private initializeCustomerList() {
    this.customers$ = this.appService.customers$.pipe(
      tap(_ => {
        this.userAssignedCustomersCount = _.length;
        if (this.userAssignedCustomersCount == 1)
          {this.selectedCustomers = [_[0].number]; }
      }),
      map((_) => _.map((x) => ({ key: x.number, value: `${x.number} - ${x.name}` })))
    );
  }

  onPageChange(event: LazyLoadEvent) {
    if (event.rows != this.numberOfRowsPerPage)
      {this.numberOfRowsPerPage = event.rows; }
    this.currentPageNumber =
      event.first == 0 ? 1 : event.first / event.rows + 1;
    this.isLoading = true;
    this.onGenerateReport();
  }

  public onSelectedCustomersChange(event: number[]) {
    if (event.length == 0) {
      this.statusesSummary = null;
    }
  }

  onGenerateReport() {
    if (this.selectedCustomers?.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoCustomersSelected');
      return false;
    }
    const parameters = this.getPaginatedReportParameters();
    this.isLoading = true;
    this.reportsService
      .getStatusReport(parameters)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((_) => {
        this.statusesSummary = _.pageItems;
        this.totalNumberOfItems = _.totalCount;
        this.totalStatuses = this.translateService.instant('Reports.Status.TotalStatuses') + _.totalCount.toString();

        if (this.statusesSummary && this.statusesSummary.length > 0 && this.currentPageNumber == 1) {
          this.statusesSummaryTotals = this.statusesSummary[0];
        }
      });
  }

    getDetails(statusCode: number) {
      const detailsParams =  this.getReportDetailsParameters(statusCode);
      const summary = this.statusesSummary.find(x => x.statusCode == statusCode);
      if (summary.cases == undefined)
      {
        this.reportsService
          .getStatusDetails(detailsParams)
          .pipe(finalize(() => (this.isLoading = false)))
          .subscribe((_) => {
            this.statusesSummary.filter(x => x.statusCode == statusCode)[0].cases = _ ;
          });
      }
    }

  private getPaginatedReportParameters(): IPaginatedSearch<ICasesStatusRequest> {
    return {
      currentPage: this.currentPageNumber,
      pageSize: this.numberOfRowsPerPage,
      filter: this.getReportParameters(),
    } as IPaginatedSearch<ICasesStatusRequest>;
  }

  getReportParameters(): ICasesStatusRequest {
    return {
      customerNumbers: this.selectedCustomers,
      calculateTotals: this.currentPageNumber == 1 ? true : false
    } as ICasesStatusRequest;
  }

  getReportDetailsParameters(statusCode: number): ICasesStatusDetailsRequest {
    return {
      customerNumbers: this.selectedCustomers,
      statusCode
    } as ICasesStatusDetailsRequest;
  }

  onExportExcelClick() {
    if (this.selectedCustomers?.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoCustomersSelected');
      return false;
    }
    const parameters = this.getReportParameters();
    this.isLoading = true;
    this.reportsService.exportStatusReportForExcel(parameters, this.translateService.getDefaultLang())
      .pipe(finalize(() => { this.isLoading = false; }), takeUntil(this.unsubscribe$))
      .subscribe(
        blob => {
          saveAs(blob, `${this.translateService.instant('Reports.Status.Title')}_${new Date().getDate() + (new Date().getMonth() + 1) + new Date().getFullYear() + '_' + new Date().getTime().toString().slice(0, 6)}.xlsx`);
        },
        error => {
          this.notifications.genericError();
        });
  }

  openCaseInNewTab(caseModel : ICaseStatusDetail)
  {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/cases/${caseModel.caseNumber}`])
    );
    window.open(url, '_blank');
  }
}
